import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, type OnDestroy, type OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { Store } from '@ngrx/store';
import { type Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, take } from 'rxjs/operators';

import { LoaderType } from '@fcom/common/';
import { GtmService } from '@fcom/common/gtm';
import type { SelectOption } from '@fcom/common/interfaces';
import { Airline } from '@fcom/common/interfaces/airlines';
import { ScrollService } from '@fcom/common/services/scroll/scroll.service';
import type { Location } from '@fcom/core-api';
import type { Profile } from '@fcom/core-api/login';
import type { AppState } from '@fcom/core/interfaces';
import { profile } from '@fcom/core/selectors';
import { LocationMatchOnline, LocationRouteCffService } from '@fcom/core/services';
import { getPartnerAirlines, LocalDate, unsubscribe } from '@fcom/core/utils';
import { finShare } from '@fcom/rx';
import {
  AUTOCOMPLETE_DEBOUNCE_TIME,
  dateValidator,
  disableConditionalFields,
  enableConditionalFields,
  FormStatus,
  locationFormatValidator,
  softTicketNumberValidator,
  trackFormState,
} from '@fcom/service-forms';
import { DatepickerTheme } from '@fcom/service-forms/components/common/datepicker.component';
import { AutocompleteTheme } from '@fcom/service-forms/components/common/locations-autocomplete.component';
import { ButtonType, ModalButtons } from '@fcom/ui-components';
import { LanguageService } from '@fcom/ui-translate';

import {
  localizationKeyGenericError,
  LoggedInMissingPointsErrorCode,
  loggedInMissingPointsErrorCodeToLocalizationKeyMap,
  LoggedInMissingPointsFormField,
  PortalServicesMailerResponseData,
} from '../interfaces';
import { MissingPointsService } from '../services/missing-points.service';
import { FLIGHT_NUMBER_PATTERN_WITHOUT_AIRLINE_CODE } from '../utils';
import { bookingClassSelectOptions } from '../utils/utils';

const CLAIM_MISSING_POINTS = 'claim-missing-points';

@Component({
  selector: 'fin-logged-in-missing-points-form',
  templateUrl: 'logged-in-missing-points-form.component.html',
  styleUrls: ['./logged-in-missing-points-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class LoggedInMissingPointsFormComponent implements OnInit, OnDestroy {
  public readonly Airline = Airline;
  public readonly AutocompleteTheme = AutocompleteTheme;
  public readonly ButtonType = ButtonType;
  public readonly DatepickerTheme = DatepickerTheme;
  public readonly FormStatus = FormStatus;
  public readonly LoaderType = LoaderType;
  public readonly LoggedInMissingPointsErrorCode = LoggedInMissingPointsErrorCode;
  public readonly LoggedInMissingPointsFormField = LoggedInMissingPointsFormField;
  public readonly ModalButtons = ModalButtons;

  @ViewChild('formContainer', { static: true })
  formContainer: ElementRef;

  destinationLoading$: Subject<boolean> = new Subject();
  departureLoading$: Subject<boolean> = new Subject();
  destinationLocations$: Subject<Location[]> = new Subject();
  departureLocations$: Subject<Location[]> = new Subject();
  profile$: Observable<Profile>;
  reactiveForm: UntypedFormGroup;
  formStatus: FormStatus = FormStatus.INITIAL;
  loggedOutMissingPointsFormSuccess = false;
  errorLocalizationKey: string;
  // TODO: Get the airline names from some common place when available
  airlines: SelectOption[];
  minDate: LocalDate;
  maxDate: LocalDate;
  bookingClasses = bookingClassSelectOptions;
  ticketNumberHelpModalOpen = false;

  private subscriptions: Subscription = new Subscription();
  private readonly otherAirlinesFields: LoggedInMissingPointsFormField[] = [
    LoggedInMissingPointsFormField.ARRIVAL_AIRPORT,
    LoggedInMissingPointsFormField.BOOKING_CLASS,
    LoggedInMissingPointsFormField.DEPARTURE_AIRPORT,
    LoggedInMissingPointsFormField.FLIGHT_DATE,
    LoggedInMissingPointsFormField.FLIGHT_NUMBER,
  ];
  private readonly japanAirlinesFields: LoggedInMissingPointsFormField[] = [
    LoggedInMissingPointsFormField.SEQUENCE_NUMBER,
  ];

  constructor(
    private fb: UntypedFormBuilder,
    private locationRouteCffService: LocationRouteCffService,
    private languageService: LanguageService,
    private scrollService: ScrollService,
    private gtmService: GtmService,
    private missingPointsService: MissingPointsService,
    private store$: Store<AppState>
  ) {
    this.profile$ = this.store$.pipe(profile(), finShare());
  }

  ngOnInit(): void {
    this.airlines = getPartnerAirlines();

    this.createForm();

    this.destinationLoading$.next(false);
    this.departureLoading$.next(false);

    this.onDestinationChanges();
    this.onDepartureChanges();

    this.watchFieldChanges(this.reactiveForm, this.onAirlineChange.bind(this), LoggedInMissingPointsFormField.AIRLINE);

    this.minDate = LocalDate.now().minusMonths(6);
    this.maxDate = LocalDate.now().minusDays(14);
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  createForm(): void {
    this.reactiveForm = this.fb.group({
      [LoggedInMissingPointsFormField.AIRLINE]: [Airline.FINNAIR, [Validators.required]],
      [LoggedInMissingPointsFormField.TICKET_NUMBER]: ['', [Validators.required, softTicketNumberValidator()]],
      [LoggedInMissingPointsFormField.NO_TICKET_NUMBER]: [false],
      [LoggedInMissingPointsFormField.FLIGHT_NUMBER]: [
        { value: '', disabled: true },
        [Validators.required, Validators.pattern(FLIGHT_NUMBER_PATTERN_WITHOUT_AIRLINE_CODE)],
      ],
      [LoggedInMissingPointsFormField.BOOKING_CLASS]: [{ value: '', disabled: true }, [Validators.required]],
      [LoggedInMissingPointsFormField.DEPARTURE_AIRPORT]: [
        { value: '', disabled: true },
        [Validators.required, locationFormatValidator()],
      ],
      [LoggedInMissingPointsFormField.ARRIVAL_AIRPORT]: [
        { value: '', disabled: true },
        [Validators.required, locationFormatValidator()],
      ],
      [LoggedInMissingPointsFormField.FLIGHT_DATE]: [
        { value: '', disabled: true },
        [Validators.required, dateValidator()],
      ],
      [LoggedInMissingPointsFormField.SEQUENCE_NUMBER]: [{ value: '', disabled: true }, [Validators.required]],
    });
  }

  onSubmit(): void {
    if (this.formStatus === FormStatus.LOADING) {
      return;
    }

    if (!this.reactiveForm.valid) {
      this.reactiveForm.markAllAsTouched();
      this.reactiveForm.updateValueAndValidity();

      return;
    }

    this.errorLocalizationKey = undefined;
    this.formStatus = FormStatus.LOADING;
    this.scrollService.scrollToImmediate(this.formContainer);
    trackFormState(CLAIM_MISSING_POINTS, 'send-attempt', this.gtmService);

    this.subscriptions.add(
      this.missingPointsService
        .loggedInMissingPointsRequest(this.reactiveForm.value)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.onFormSubmitSuccess();
          },
          // eslint-disable-next-line rxjs/no-implicit-any-catch
          error: (response: HttpErrorResponse) => {
            this.onFormSubmitFailed(response);
          },
        })
    );
  }

  onLoggedOutMissingPointsFormComplete(response: PortalServicesMailerResponseData): void {
    if (response?.returnValue === '1') {
      this.loggedOutMissingPointsFormSuccess = true;
    }
  }

  showHelp(event: Event): void {
    event.preventDefault();
    this.ticketNumberHelpModalOpen = true;
  }

  private onAirlineChange(airline: string): void {
    switch (airline) {
      case Airline.FINNAIR:
        disableConditionalFields(this.otherAirlinesFields, this.reactiveForm);
        disableConditionalFields(this.japanAirlinesFields, this.reactiveForm);
        break;
      case Airline.JAPAN_AIRLINES:
        enableConditionalFields(this.otherAirlinesFields, this.reactiveForm);
        enableConditionalFields(this.japanAirlinesFields, this.reactiveForm);
        break;
      default:
        enableConditionalFields(this.otherAirlinesFields, this.reactiveForm);
        disableConditionalFields(this.japanAirlinesFields, this.reactiveForm);
    }
  }

  private queryLocations(queryString: string, locations$: Subject<Location[]>, loading$: Subject<boolean>) {
    const locale = this.languageService.localeValue;

    loading$.next(true);

    this.subscriptions.add(
      this.locationRouteCffService
        .locationMatchesFor(queryString, locale, LocationMatchOnline.ALL)
        .subscribe((matchedLocations) => {
          loading$.next(false);
          locations$.next(matchedLocations.items);
        })
    );
  }

  private onDepartureChanges(): void {
    this.subscriptions.add(
      this.reactiveForm
        .get(LoggedInMissingPointsFormField.DEPARTURE_AIRPORT)
        .valueChanges.pipe(debounceTime(AUTOCOMPLETE_DEBOUNCE_TIME))
        .subscribe((val) => {
          if (this.reactiveForm.get(LoggedInMissingPointsFormField.DEPARTURE_AIRPORT).status === 'INVALID') {
            this.queryLocations(val, this.departureLocations$, this.departureLoading$);
          }
        })
    );
  }

  private onDestinationChanges(): void {
    this.subscriptions.add(
      this.reactiveForm
        .get(LoggedInMissingPointsFormField.ARRIVAL_AIRPORT)
        .valueChanges.pipe(debounceTime(AUTOCOMPLETE_DEBOUNCE_TIME))
        .subscribe((val) => {
          if (this.reactiveForm.get(LoggedInMissingPointsFormField.ARRIVAL_AIRPORT).status === 'INVALID') {
            this.queryLocations(val, this.destinationLocations$, this.destinationLoading$);
          }
        })
    );
  }

  private watchFieldChanges(form: UntypedFormGroup, callback: Function, field: string): void {
    this.subscriptions.add(
      form.get(field).valueChanges.subscribe((val) => {
        callback(val);
      })
    );
  }

  private onFormSubmitSuccess(): void {
    trackFormState(CLAIM_MISSING_POINTS, 'success', this.gtmService);
    this.formStatus = FormStatus.COMPLETE;
  }

  private onFormSubmitFailed(response: HttpErrorResponse): void {
    this.formStatus = FormStatus.ERROR;
    const errorCode: LoggedInMissingPointsErrorCode = response.error.error?.errorCode;
    this.errorLocalizationKey =
      loggedInMissingPointsErrorCodeToLocalizationKeyMap[errorCode] || localizationKeyGenericError;

    if (errorCode !== LoggedInMissingPointsErrorCode.PASSENGER_IS_AN_ID_PASSENGER) {
      this.reactiveForm.get(LoggedInMissingPointsFormField.NO_TICKET_NUMBER).setValue(true);
    }
    trackFormState(CLAIM_MISSING_POINTS, 'error', this.gtmService);
  }
}
